/**
 * This file is responsible for rendering the home page in the application.
 *
 * The component uses server-side rendering (getServerSideProps) to fetch the translations for the page.
 *
 *
 *
 * The Home component from '~/ui/pages/Home' is used to render the home page content.
 *
 * On mount, the component checks for the existence of certain cookies (COOKIE_STOP_REDIRECT, COOKIE_LANGUAGE_NAME, COOKIE_LOCALITY_NAME).
 * If the cookies exist, the component redirects to the path specified in the COOKIE_LANGUAGE_NAME cookie.
 * If the cookies do not exist, the component sets the COOKIE_LANGUAGE_NAME cookie to the browser's language (if it's supported) or 'en' (if it's not supported), and redirects to the path specified in the COOKIE_LANGUAGE_NAME cookie.
 *
 * If there's an error during server-side rendering, it is logged to the console.
 *
 * The NextHead component from '~/ui/layouts/NextHead' is used to set the meta tags for the page.
 * The Head component from 'next/head' is used to set the canonical URL and alternate URLs for the page.
 */

import { GetServerSideProps } from 'next';
import { useTranslation } from 'next-i18next';
import { serverSideTranslations } from 'next-i18next/serverSideTranslations';
import * as siteConfig from '~/constants/siteConfig';
import { wrapper } from '~/store';
import { NextPage } from '@Hotels-at-Home/hah-fe-core/dist/components';
import Home from '../ui/pages/Home';
import {
  COOKIE_LOCALITY_NAME,
  COOKIE_PROMO_TAG,
  COOKIE_STOP_REDIRECT,
} from '@Hotels-at-Home/hah-fe-core/dist/constants';
import { getCookie, setCookie } from '@Hotels-at-Home/hah-fe-core/dist/utils/cookies';
import { useEffect } from 'react';

import { useRouter } from 'next/router';
import Head from 'next/head';
import { homepageHrefLangTags } from '~/utils/hreflangs';
import {
  getHomepageRedirect,
  shouldRedirect,
} from '@Hotels-at-Home/hah-fe-core/dist/utils/redirect';
import languageParser from 'accept-language-parser';
import { getStructuredData } from '@Hotels-at-Home/hah-fe-core/dist/utils/schema';
import { IGetPromotionResponse } from '@Hotels-at-Home/hah-fe-core/dist/types';
import { publicQuery, GetPromotion } from '@Hotels-at-Home/hah-fe-core/dist/services/graphql';
import apolloConfig from '~/constants/apolloConfig';

interface IProps {
  promotion: IGetPromotionResponse;
}

const HomePage = () => {
  const { t } = useTranslation('', { keyPrefix: '' });
  const { siteName } = siteConfig;
  const siteUrl = process.env.NEXT_PUBLIC_SITE_URL;

  const router = useRouter();

  const meta = {
    title: t('$local.$meta.home_title', { siteName }),
    description: t('$local.$meta.home_description', { siteName }),
    url: `${siteUrl}/${router.locale}`,
    image: `${siteUrl}${siteConfig.defaultOgImage}`,
  };

  const structuredData = getStructuredData();

  structuredData.url = siteUrl;

  const onMount = (): void => {
    if (
      Object.prototype.hasOwnProperty.call(router.query, 'referrer') ||
      Object.prototype.hasOwnProperty.call(router.query, '___store')
    ) {
      setCookie(COOKIE_STOP_REDIRECT, '1');
      return null;
    }
    return null;
  };

  useEffect(() => {
    onMount();
  }, [router.pathname]);

  return (
    <NextPage
      error=""
      meta={meta}
      headContent={
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{ __html: JSON.stringify(structuredData) }}
        />
      }
      siteUrl={siteUrl}
    >
      <Head>
        <link rel="canonical" href={`${siteUrl}/${router.locale}`} />
        <link
          rel="preload"
          as="image"
          type="image/webp"
          href="/images/homePage/heroSection/background-mob.webp"
          fetchPriority="high"
          media="(max-width: 660px)"
        />
        <link
          rel="preload"
          as="image"
          type="image/webp"
          href="/images/homePage/heroSection/background.webp"
          fetchPriority="high"
          media="(min-width: 661px)"
        />
        {homepageHrefLangTags.map(
          ({ href, hrefLang }) =>
            href && (
              <link key={`${hrefLang} ${href}`} rel="alternate" hrefLang={hrefLang} href={href} />
            ),
        )}
      </Head>
      <Home />
    </NextPage>
  );
};

export const getServerSideProps: GetServerSideProps = wrapper.getServerSideProps(
  () => async ctx => {
    const localityCookie = getCookie(COOKIE_LOCALITY_NAME, ctx);
    const userLanguage = ctx.req.headers['accept-language'] as string;

    if (shouldRedirect(ctx)) {
      const parsedLanguage = languageParser.parse(userLanguage)[0];
      const localityParam = ctx.query.locality; // can be used to override the locality header for testing
      const localityHeader =
        (ctx.req.headers.Locality as string) || (ctx.req.headers.locality as string);
      const desiredLocale = localityParam ? localityParam.toString().toUpperCase() : localityHeader;

      // TODO: Refactor to include in the same flow as en-us
      if (parsedLanguage?.code === 'zh') {
        const newRedirect = `${parsedLanguage.code}-cn`;
        const hrefData = homepageHrefLangTags.find(item => item.hrefLang === newRedirect);
        const destination = hrefData.href;

        return {
          redirect: {
            destination,
            permanent: false,
          },
        };
      }

      const hrefRedirect = getHomepageRedirect(
        (localityCookie as string) || (desiredLocale as string),
      );

      // Only redirect if going outside EU; Internal redirects are handled in the middleware
      if (
        hrefRedirect !== 'en-gb' &&
        hrefRedirect !== 'fr' &&
        hrefRedirect !== 'de' &&
        hrefRedirect !== 'es'
      ) {
        const hrefData = homepageHrefLangTags.find(item => item.hrefLang === hrefRedirect);
        const defaultHref = homepageHrefLangTags.find(item => item.hrefLang === 'en');
        return {
          redirect: {
            destination: hrefData.href ?? defaultHref.href,
            permanent: false,
          },
        };
      }
    }

    let promotion = null;
    const promoTag = getCookie(COOKIE_PROMO_TAG, ctx);
    try {
      const promotionReq: Promise<{ data: { getpromo: IGetPromotionResponse } }> = publicQuery(
        apolloConfig(),
        {
          query: GetPromotion,
        },
        'GetPromotion',
        ctx,
        true,
      );
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const promisesList: Promise<any>[] = [promotionReq];
      const [promotionRes] = await Promise.all(promisesList);
      promotion = {
        ...promotionRes.data.getpromo,
        employeeSite: promoTag === 'employeesite' || ctx?.query?.['promo-tag'] === 'employeesite',
      };
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (e: any) {
      // eslint-disable-next-line no-console
      console.log(e);
    }

    return {
      props: {
        ...(await serverSideTranslations(ctx.locale)),
        promotion,
      },
    };
  },
);

export default HomePage;
